import { useAuthState } from '@/app/providers/auth'
import { VendorSearchState } from '@/components/page-specific/catering/suppliers/states/useVendorSearchState'
import { alpha, Box, Button, Typography } from '@mui/material'

import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import SupplierCardItem from '../supplier-card-item/supplier-card-item'
import SupplierListSkeleton from './supplier-list-skeleton'

type Props = {
  pageInfo: VendorSearchState['pageInfo']
  vendors: VendorSearchState['vendors']
  called: boolean
  searching: boolean
  filters: VendorSearchState['filters']
  search: VendorSearchState['search']
  defaultSearch: VendorSearchState['defaultSearch']
}

const SupplierList: React.FC<Props> = ({
  pageInfo,
  vendors,
  searching,
  filters,
  search,
  called,
}) => {
  const searchParams = useSearchParams()
  const authState = useAuthState()
  const [displayVendors, setDisplayVendors] = useState(vendors)
  const results = Boolean(displayVendors.length)
  const { t } = useTranslation()

  const selectedHighlight = filters.highlightsTags?.[0] ?? ''

  useEffect(() => {
    if (!searching && pageInfo?.offset && pageInfo.offset > 0) {
      setDisplayVendors((prev) => {
        const next = [...prev]
        const existingIds = new Set(prev.map((vendor) => vendor.id))
        vendors.forEach((vendor) => {
          if (!existingIds.has(vendor.id)) {
            next.push(vendor)
            existingIds.add(vendor.id)
          }
        })
        return next
      })
    } else {
      setDisplayVendors(vendors)
    }
  }, [vendors, pageInfo?.offset, searching])

  const handleShowMore = () => {
    search({ offset: displayVendors.length })
  }

  const gridTemplateColumns = useMemo(
    () => ({
      xs: 'repeat(1, minmax(0, 1fr))',
      sm: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(2, minmax(0, 1fr))',
      lg: 'repeat(3, minmax(0, 1fr))',
      xl: 'repeat(4, minmax(0, 1fr))',
    }),
    [],
  )

  const tileSource = filters.itemSearch ? 'item-search' : 'supplier-tile'

  return (
    <>
      {(results || selectedHighlight) && (
        <>
          <Typography variant="h4" gutterBottom>
            {selectedHighlight || t('marketplace:all')}{' '}
            {t('marketplace:vendorsCount', { count: pageInfo?.count })}
            {selectedHighlight ? (
              <Button size="small" variant="text" onClick={() => search({ highlightsTags: [] })}>
                {t('marketplace:backToAll')}
              </Button>
            ) : null}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gap: { xs: 2, md: 3 },
              gridTemplateColumns,
              width: '100%',
            }}>
            {displayVendors.map((vendor) => {
              const linkQueryParams = {
                source: tileSource,
                preventAutoBasketSelection: 'true',
                ...(filters.itemSearch ? { itemSearchTerm: filters.itemSearch } : {}),
              } as const

              const query = Object.fromEntries((searchParams ?? []).entries())
              return (
                <Box
                  key={vendor.id}
                  sx={(theme) => ({
                    minWidth: 0,
                    ...(vendor.itemSearchMetadata && {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      borderRadius: 2,
                      padding: 1,
                      boxShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.45)}`,
                    }),
                  })}>
                  <Link
                    href={{
                      pathname: `/office-catering/vendors/${vendor.permalink}`,
                      query: { ...linkQueryParams, ...query },
                    }}
                    style={{ display: 'block', height: '100%', width: '100%' }}>
                    <SupplierCardItem
                      vendor={vendor}
                      enableFavourite={authState.type === 'authenticated'}
                    />
                  </Link>
                </Box>
              )
            })}
          </Box>
        </>
      )}
      {called && !searching && !results && (
        <Box sx={{ mt: 2 }} textAlign="center">
          <Typography variant="body1">{t('marketplace:oopsLooksLikeNoVendors')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('marketplace:removeTheFiltersToViewAllVendors')}
          </Typography>
        </Box>
      )}
      {searching && (
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, md: 3 },
            gridTemplateColumns,
            width: '100%',
            mt: 2,
          }}>
          {Array.from({ length: 8 }, (_, skeletonNumber) => skeletonNumber + 1).map(
            (skeletonNumber) => (
              <SupplierListSkeleton key={`vendor-skeleton-${skeletonNumber}`} />
            ),
          )}
        </Box>
      )}
      {pageInfo?.hasMore && !searching && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button variant="contained" onClick={handleShowMore}>
            {t('marketplace:showMore')}
          </Button>
        </Box>
      )}
    </>
  )
}

export default SupplierList
